/**
 * A segmented control — a small set of mutually exclusive choices drawn as one
 * pill. The kit has no such control yet; this is a radio group underneath
 * (`role="radiogroup"`, arrow keys move the choice) so it behaves as one to a
 * keyboard and a screen reader, and it is used where a choice is between two
 * or three short words (T-5.39's identifiers).
 */

import type { KeyboardEvent, ReactNode } from 'react'

export interface SegmentedOption<T extends string> {
  value: T
  label: ReactNode
}

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string
  value: T
  options: SegmentedOption<T>[]
  onChange: (next: T) => void
  disabled?: boolean
}) {
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0
    if (!delta || disabled) return
    event.preventDefault()
    const index = options.findIndex((option) => option.value === value)
    const next = options[(index + delta + options.length) % options.length]
    if (next) onChange(next.value)
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={move}
      className="bg-muted inline-flex w-full rounded-xl p-1 sm:w-auto"
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`focus-visible:ring-primary/40 flex-1 rounded-lg px-4 py-2 text-sm font-medium whitespace-nowrap transition focus-visible:ring-2 focus-visible:outline-none disabled:opacity-60 sm:flex-none ${
              active
                ? 'text-foreground bg-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
